import styled from '@emotion/styled';
import { useMemo, Fragment, useEffect, type ReactNode, Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { FreeTextbooksTopicSubtopicTreeQuery } from '__free_textbooks_generated__/FreeTextbooksTopicSubtopicTreeQuery.graphql';
import { ErrorBoundary } from 'ms-components/ErrorBoundary/ErrorBoundary';
import { AccordionItem } from 'ms-pages/Teacher/components/Accordion';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { fontFamily, fontWeight } from 'ms-styles/base';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import _ from 'ms-utils/relay/extractNode';

import { NoSearchResults } from './assets/NoSearchResults';
import type { Subtopic, Topic, SupportedColorValues } from './types';

export type { Subtopic, Topic };

graphql`
  fragment FreeTextbooksTopicSubtopicTree_topic on FreeTextbookTopic
  @relay(mask: false) {
    id
    title
    badgeUrl
    syllabus {
      textbookType
    }
  }
`;

graphql`
  fragment FreeTextbooksTopicSubtopicTree_subtopic on FreeTextbookSubtopic
  @relay(mask: false) {
    id
    title
    hasExercise
    hasLessons
    hasInvestigations
    hasWorksheet
  }
`;

const FREE_TEXTBOOKS_TOPIC_SUBTOPIC_TREE_QUERY = graphql`
  query FreeTextbooksTopicSubtopicTreeQuery(
    $syllabusId: ID!
    $isSearch: Boolean!
    $search: String!
  ) {
    syllabus(id: $syllabusId) @skip(if: $isSearch) {
      id
      title
      thumbnailImageUrl
      overview {
        id
      }
      topics(first: 1000) {
        edges {
          node {
            ...FreeTextbooksTopicSubtopicTree_topic @relay(mask: false)
            subtopics(first: 1000) {
              edges {
                node {
                  ...FreeTextbooksTopicSubtopicTree_subtopic @relay(mask: false)
                }
              }
            }
          }
        }
      }
    }

    syllabusSearchResult(syllabusId: $syllabusId, query: $search)
      @include(if: $isSearch) {
      id
      topics(first: 1000) {
        edges {
          node {
            topic {
              ...FreeTextbooksTopicSubtopicTree_topic @relay(mask: false)
            }
            subtopics(first: 1000) {
              edges {
                node {
                  subtopic {
                    ...FreeTextbooksTopicSubtopicTree_subtopic
                      @relay(mask: false)
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const Root = styled.div({
  fontFamily: fontFamily.body,
});

const SubtopicWrapper = styled.div({
  paddingRight: BASE_UNIT * 2,
  paddingTop: BASE_UNIT,
  paddingBottom: 0,
});

const EmptyContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const EmptyTitle = styled.div({
  fontFamily: fontFamily.body,
  fontWeight: fontWeight.semibold,
  textAlign: 'center',
  padding: 2 * BASE_UNIT,
});

const EmptyDescription = styled.div({
  fontFamily: fontFamily.body,
  textAlign: 'center',
  padding: 2 * BASE_UNIT,
});

const TopicWrapper = styled.div({
  fontFamily: fontFamily.body,
  marginBottom: 3 * BASE_UNIT,
  minHeight: 25,
});

type Props = {
  isTopicOpen: (topicId: string) => boolean;
  onOpenTopicId?: ((topicId: string) => void) | undefined;
  renderTopicLabel: (topic: Topic, search: string) => ReactNode;
  renderSubtopicLabel: (
    topic: Topic,
    subtopic: Subtopic,
    search: string,
  ) => ReactNode;
  renderTopicOverviewLabel?: ((topic: Topic) => ReactNode) | null | undefined;
  search: string;
  syllabusId: string;
  topicFilter?: ((topic: Topic) => boolean) | undefined;
  subtopicFilter?: ((subtopic: Subtopic, topic: Topic) => boolean) | undefined;
  color: SupportedColorValues;
  onReady?: (() => void) | undefined;
};

const EmptyState = ({
  color,
  search,
}: {
  color: SupportedColorValues;
  search: string;
}) => (
  <EmptyContainer>
    <NoSearchResults fillColor={color} />
    <EmptyTitle style={{ color }}>No matches found for '{search}'</EmptyTitle>
    <EmptyDescription style={{ color }}>
      Try adjusting your search or selecting another textbook
    </EmptyDescription>
  </EmptyContainer>
);

function SubtopicList({
  topics,
  renderTopicLabel,
  renderSubtopicLabel,
  renderTopicOverviewLabel,
  onOpenTopicId,
  onReady = () => {},
  isTopicOpen,
  search,
  topicFilter = () => true,
  subtopicFilter = () => true,
  color,
}: Props & {
  topics: ReadonlyArray<Topic>;
}) {
  useEffect(() => {
    onReady();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (topics.length === 0) {
    return <EmptyState color={color} search={search} />;
  }
  // build out the accordion
  return (
    <>
      {topics.filter(topicFilter).map(topic => {
        const topicLabel = renderTopicLabel(topic, search);
        if (topicLabel == null) return null;
        return (
          <TopicWrapper
            key={topic.id}
            onClick={() => onOpenTopicId != null && onOpenTopicId(topic.id)}
          >
            {topicLabel}
            <AccordionItem isOpen={isTopicOpen(topic.id) || search !== ''}>
              <SubtopicWrapper>
                {/* Topic Overview things */}
                {topic.syllabus.textbookType === 'CORE_TEXTBOOK' &&
                search === '' ? (
                  renderTopicOverviewLabel ? (
                    <Fragment>{renderTopicOverviewLabel(topic)}</Fragment>
                  ) : null
                ) : null}
                {topic.subtopics.edges
                  .filter(subtopic => subtopicFilter(subtopic.node, topic))
                  .map(subtopic => (
                    <Fragment key={subtopic.node.id}>
                      {renderSubtopicLabel(topic, subtopic.node, search)}
                    </Fragment>
                  ))}
              </SubtopicWrapper>
            </AccordionItem>
          </TopicWrapper>
        );
      })}
    </>
  );
}

export function FreeTextbooksTopicSubtopicTree(props: Props) {
  return (
    <ErrorBoundary
      name="FreeTextbooksTopicSubtopicTree"
      fallback="Error loading topics"
    >
      <Suspense fallback={<MinorSpinner />}>
        <FreeTextbooksTopicSubtopicTreeInner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

/*
 * Self contained subtopic selector that makes a query to fetch the list of subtopics
 * Because of the nature of the store, this query may be free in some cases
 */
function FreeTextbooksTopicSubtopicTreeInner({
  syllabusId,
  search,
  ...props
}: Props) {
  // String tokenisation + search results in search strings with trailing
  // whitespace matching everything; we trim the search string to avoid that.
  const trimmedSearch = search
    .split(' ')
    .filter(s => s !== '')
    .join(' ');

  const data = useLazyLoadQuery<FreeTextbooksTopicSubtopicTreeQuery>(
    FREE_TEXTBOOKS_TOPIC_SUBTOPIC_TREE_QUERY,
    useMemo(
      () => ({
        syllabusId,
        isSearch: !!trimmedSearch,
        search: trimmedSearch,
      }),
      [syllabusId, trimmedSearch],
    ),
    {
      networkCacheConfig: {
        // @ts-expect-error TODO fix this once we upgrade relay and the types are sound
        useCache: true,
      },
    },
  );
  const { syllabus, syllabusSearchResult } = data;

  let topics: ReadonlyArray<Topic> = [];
  if (trimmedSearch === '' && syllabus != null) {
    topics = _(syllabus.topics);
  } else if (trimmedSearch !== '' && syllabusSearchResult != null) {
    // The code below reconstructs the TopicResult object to have the same shape
    // of the Topic object so the underlying component can work trasparently with them
    topics = _(syllabusSearchResult.topics).map(({ topic, subtopics }) => ({
      ...topic,
      subtopics: {
        edges: _(subtopics).map(({ subtopic }) => ({
          node: subtopic,
        })),
      },
    }));
  }

  return (
    <Root>
      <SubtopicList
        search={trimmedSearch}
        syllabusId={syllabusId}
        {...props}
        topics={topics}
      />
    </Root>
  );
}
